import { ButtonSizes, ButtonTypes } from '@app/components/platform/button/button.component';
import { TaxReturnSummary } from '@app/models/payroll/tax-return-summary.abstract.model';
import { Translatable, TranslatableKey } from '@app/types/translatable.type';
import { BadgeColumn } from '@table/classes/badge-column.class';
import { ButtonColumn } from '@table/classes/button-column.class';
import { Column } from '@table/classes/column.class';
import { CellDisplay } from '@table/enums/cell-display.enum';
import { BadgeDisplay } from '@table/interfaces/badge-display.interface';

export type Event = `${SummaryKey}_${Action}`;

type SummaryKey = 'T4' | 'RL1';
type Action = 'generate' | 'finalize';

const getSummaryKey = (summary: TaxReturnSummary): SummaryKey => (summary.isT4Summary() ? 'T4' : 'RL1');

export class PackageColumn extends Column {
    title = 'table.employerTaxPackages.package';
    type = CellDisplay.string;
    sortable = true;

    getDisplayPropertyValue(summary: TaxReturnSummary): Translatable {
        return {
            key: `table.employerTaxPackages.${getSummaryKey(summary)}.packageName${summary.isAmendment ? 'Amended' : ''}`,
            params: { year: summary.year },
        };
    }
}

export class AgencyColumn extends Column {
    title = 'table.employerTaxPackages.agency';
    type = CellDisplay.string;
    sortable = true;

    getDisplayPropertyValue(summary: TaxReturnSummary): string {
        return `table.employerTaxPackages.${getSummaryKey(summary)}.agency`;
    }
}

export class StatusColumn extends BadgeColumn<TaxReturnSummary> {
    title = 'table.employerTaxPackages.status';
    sortable = true;

    getBadgeDisplay(summary: TaxReturnSummary): BadgeDisplay | undefined {
        return summary.stateBadge;
    }

    getCellText(summary: TaxReturnSummary): string {
        return summary.id ? '' : '-';
    }
}

export class SubmitDateColumn extends Column {
    title = 'table.employerTaxPackages.submitDate';
    type = CellDisplay.date;
    sortable = true;
    property = 'submittedAt';

    getDisplayPropertyValue(summary: TaxReturnSummary): string {
        return summary.submittedAt ?? '-';
    }
}

export class CompleteDateColumn extends Column {
    title = 'table.employerTaxPackages.completeDate';
    type = CellDisplay.date;
    sortable = true;
    property = 'completedAt';

    getDisplayPropertyValue(summary: TaxReturnSummary): string {
        return summary.completedAt ?? '-';
    }
}
export class ActionButton extends ButtonColumn<TaxReturnSummary> {
    title = '';
    buttonType: ButtonTypes = 'primary';
    buttonSize: ButtonSizes = 'mobile-shrink';

    constructor() {
        super();
    }

    disabled(summary: TaxReturnSummary): boolean {
        // Should never be disabled if model doesn't exist yet
        if (!summary.id) return false;

        return !summary.canSubmit;
    }

    disabledText(summary: TaxReturnSummary): TranslatableKey | null {
        if (summary.isErrored) {
            return 'table.employerTaxPackages.pleaseFixTheErrorsBeforeSubmitting';
        }

        if (summary.isAmendment) {
            return 'table.employerTaxPackages.pleaseAmendEachAddedFormBeforeSubmitting';
        }

        return null;
    }

    getButtonText(summary: TaxReturnSummary): TranslatableKey {
        return summary.id
            ? 'table.employerTaxPackages.finalizeAndSubmit'
            : `table.employerTaxPackages.${getSummaryKey(summary)}.generate`;
    }

    getEvent(summary: TaxReturnSummary): Event {
        return `${getSummaryKey(summary)}_${summary.id ? 'finalize' : 'generate'}`;
    }

    showIf(summary: TaxReturnSummary): boolean {
        return summary.isOpen;
    }
}
